import { gql } from "lib/apolloClient";
import {
  courseFlowResourceFragment,
  courseFlowOutlineFragment,
  learningCourseItemFragment,
  assessmentFragment,
  attachmentFragment,
  unitPlanFragment,
  unitPlanDetailsFragment,
} from "./CourseFlowFragments";

export const getEntireCourseFlowFeedQuery = gql`
  query getEntireCourseFlowFeed(
    $id: ID!
    $filters: LearningCourseFlowFilter
    $type: ENTITY_TYPE_ENUM!
    $stateFilters: LearningCourseFlowStateFilter
    $syncedBluePrintItemFilters: SyncedBluePrintItemFilter
  ) {
    node(id: $id, type: $type) {
      id
      ... on Course {
        classFlowFeedWrapper {
          learningCourseFlowFeed(first: 10000, filters: $filters) {
            edges {
              node {
                ...courseFlowResource
              }
            }
            pageInfo {
              hasNextPage
              hasPreviousPage
              startCursor
              endCursor
            }
            resourceBasedCount {
              totalCount
            }
          }
        }
      }
      ... on LearningCourse {
        id
        learningCourseFlowFeedWrapper {
          learningCourseFlowFeed(first: 10000, filters: $filters) {
            edges {
              node {
                ...courseFlowResource
              }
            }
            pageInfo {
              hasNextPage
              hasPreviousPage
              startCursor
              endCursor
            }
            resourceBasedCount {
              totalCount
            }
          }
        }
        __typename
      }
      __typename
    }
  }

  ${courseFlowResourceFragment.basicDetails}
`;

export const getCourseFlowOutlineQuery = gql`
  query getCourseFlowOutline(
    $id: ID!
    $filters: LearningCourseFlowFilter
    $type: ENTITY_TYPE_ENUM!
  ) {
    node(id: $id, type: $type) {
      ... on Course {
        id
        classFlowFeedWrapper {
          learningCourseFlowFeedOutline(filters: $filters) {
            ...courseFlowOutlineItem
          }
        }
        __typename
      }
      ... on LearningCourse {
        id
        learningCourseFlowFeedWrapper {
          learningCourseFlowFeedOutline(filters: $filters) {
            ...courseFlowOutlineItem
          }
        }
        __typename
      }
      __typename
    }
  }

  ${courseFlowOutlineFragment}
`;

export const getCourseFlowMultiNodeQuery = gql`
  query getCourseFlowMultiNode(
    $ids: [ID!]
    $assessmentAssignmentsFilters: AssessmentAssignmentsFilters
    $studentIds: [ID!]
    $skipAssignedStudents: Boolean! = true
    $stateFilters: LearningCourseFlowStateFilter
    $filters: LearningCourseFlowFilter
    $syncedBluePrintItemFilters: SyncedBluePrintItemFilter
  ) {
    multiNode(ids: $ids, type: LEARNING_COURSE_FLOW) {
      id
      ... on LearningCourseFlow {
        ...courseFlowResource
      }
    }
  }
  ${courseFlowResourceFragment.completeDetails}
`;

export const getLearningCoursesQuery = gql`
  query getLearningCourses(
    $organizationId: ID!
    $after: String
    $filters: LearningCourseFilters
    $gradeFilters: LearningCourseGradeFilters
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      id
      ... on Organization {
        id
        learningCourses(first: 50, after: $after, filters: $filters) {
          edges {
            node {
              ...learningCourseItem
            }
          }
          pageInfo {
            hasNextPage
            endCursor
          }
          count {
            totalCount
          }
        }
      }
    }
  }
  ${learningCourseItemFragment}
`;

export const getImportCourseFlowFeedQuery = gql`
  query getImportCourseFlowFeed(
    $learningCourseId: ID!
    $filters: LearningCourseFlowFilter
    $assessmentAssignmentsFilters: AssessmentAssignmentsFilters
    $studentIds: [ID!]
    $skipAssignedStudents: Boolean! = true
    $syncedBluePrintItemFilters: SyncedBluePrintItemFilter
    $after: String
  ) {
    node(id: $learningCourseId, type: LEARNING_COURSE) {
      id
      ... on LearningCourse {
        id
        learningCourseFlowFeedWrapper {
          learningCourseFlowFeed(first: 100, after: $after, filters: $filters) {
            edges {
              node {
                id
                label
                depth
                resourceType: itemType
                parent {
                  id
                }
                item {
                  ... on Assessment {
                    ...assessmentItem
                  }
                  ... on Attachment {
                    ...attachmentItem
                  }
                  ... on UnitPlan {
                    ...unitPlanItem
                  }
                }
                state
                childrenHierarchyInfo(filters: $filters) {
                  maxDepth
                  totalCount
                  totalCountWithFolder
                }
              }
            }
            pageInfo {
              hasNextPage
              hasPreviousPage
              startCursor
              endCursor
            }
            resourceBasedCount {
              totalCount
            }
          }
        }
        __typename
      }
      __typename
    }
  }

  ${assessmentFragment}
  ${attachmentFragment}
  ${unitPlanFragment}
`;

export const getCompleteCourseFlowFeedQuery = gql`
  query getCompleteCourseFlowFeed(
    $learningCourseId: ID!
    $filters: LearningCourseFlowFilter
  ) {
    node(id: $learningCourseId, type: LEARNING_COURSE) {
      id
      ... on LearningCourse {
        id
        learningCourseFlowFeedWrapper {
          learningCourseFlowFeed(first: 10000, filters: $filters) {
            edges {
              node {
                id
              }
            }
          }
        }
        __typename
      }
      __typename
    }
  }
`;

export const getUnitPlanDetailsQuery = gql`
  query getUnitPlan($id: ID!, $responseFilter: ResponseFilter) {
    node(id: $id, type: UNIT_PLAN) {
      id
      ... on UnitPlan {
        id
        ...unitPlanItem
      }
    }
  }
  ${unitPlanDetailsFragment.unitPlanDetails}
`;

export const getLearningCourseClassesQuery = gql`
  query getLearningCourseClasses(
    $id: ID!
    $classFilters: LearningCourseClassFilter
  ) {
    node(id: $id, type: LEARNING_COURSE) {
      id
      ... on LearningCourse {
        id
        classes(filters: $classFilters) {
          id
          title
          isActive
          isArchived
          profileImageData {
            icon
            color
            acronym
          }
          students {
            totalCount
            edges {
              node {
                id
              }
            }
          }
        }
      }
    }
  }
`;

export const getLearningCoursesByIdsQuery = gql`
  query getLearningCoursesByIds(
    $ids: [ID!]
    $gradeFilters: LearningCourseGradeFilters
  ) {
    multiNode(ids: $ids, type: LEARNING_COURSE) {
      id
      ... on LearningCourse {
        ...learningCourseItem
      }
    }
  }
  ${learningCourseItemFragment}
`;
